import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/Logo";
import { GoogleButton } from "@/components/GoogleButton";
import { AlertCircle, Package, Truck, UserRound } from "lucide-react";
import { toast } from "sonner";

const VEHICLE_TYPES = [
  "Mini Truck (Tata Ace)", "Pickup (7ft)", "LCV 14ft", "17ft Truck", "19ft Truck",
  "22ft Truck", "32ft SXL Container", "32ft MXL Container", "Trailer", "Bus", "Car",
];

const INDIAN_STATES = [
  "Andhra Pradesh", "Assam", "Bihar", "Chhattisgarh", "Delhi", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Odisha", "Punjab", "Rajasthan", "Tamil Nadu", "Telangana", "Uttar Pradesh",
  "Uttarakhand", "West Bengal",
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const roleParam = params.get("role");

  const initial = ["sender", "lorry_owner", "driver"].includes(roleParam) ? roleParam : "sender";
  const [role, setRole] = useState(initial);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", company: "", city: "",
    vehicle_type: "", vehicle_number: "", vehicle_capacity_kg: "",
    dl_number: "", dl_reg_date: "", home_state: "", home_city: "", experience_years: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (["sender", "lorry_owner", "driver"].includes(roleParam)) setRole(roleParam);
  }, [roleParam]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        phone: form.phone.trim(),
        role,
        company: form.company.trim(),
        city: form.city.trim(),
      };
      if (role === "lorry_owner") {
        payload.vehicle_type = form.vehicle_type;
        payload.vehicle_number = form.vehicle_number.toUpperCase();
        payload.vehicle_capacity_kg = form.vehicle_capacity_kg ? Number(form.vehicle_capacity_kg) : 0;
      }
      if (role === "driver") {
        payload.dl_number = form.dl_number.toUpperCase();
        payload.dl_reg_date = form.dl_reg_date;
        payload.home_state = form.home_state;
        payload.home_city = form.home_city;
        payload.experience_years = form.experience_years ? Number(form.experience_years) : 0;
      }
      const u = await register(payload);
      toast.success(`Welcome, ${u.name}!`);
      const path = u.role === "sender" ? "/sender" : u.role === "lorry_owner" ? "/owner" : "/driver";
      nav(path);
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex mb-10"><Logo /></Link>

        <div className="overline text-[#FF5A00]">CREATE ACCOUNT</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mt-3 tracking-tight">Pick your side of the yard.</h1>

        <div className="mt-8 grid sm:grid-cols-3 gap-3">
          <RoleTile active={role === "sender"} onClick={() => setRole("sender")} icon={Package}
            title="I'm a Sender" desc="Post loads & get bids" testId="role-sender-tile" />
          <RoleTile active={role === "lorry_owner"} onClick={() => setRole("lorry_owner")} icon={Truck}
            title="Lorry Owner" desc="Bid on consignments" testId="role-owner-tile" />
          <RoleTile active={role === "driver"} onClick={() => setRole("driver")} icon={UserRound}
            title="Driver" desc="Get hired for trips" accent testId="role-driver-tile" />
        </div>

        <div className="mt-6">
          <GoogleButton role={role} testId="google-register-btn"
            label={`Continue with Google as ${role === "sender" ? "Sender" : role === "lorry_owner" ? "Lorry Owner" : "Driver"}`} />
        </div>

        <div className="mt-6 relative">
          <div className="absolute inset-x-0 top-1/2 border-t border-[#2A2A2A]"></div>
          <div className="relative flex justify-center">
            <span className="bg-[#0A0A0A] px-3 overline text-zinc-500">Or sign up with email</span>
          </div>
        </div>

        <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-5" data-testid="register-form">
          <Field label="Full name">
            <Input required value={form.name} onChange={set("name")} data-testid="reg-name-input"
              className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
          </Field>
          <Field label="Phone">
            <Input required value={form.phone} onChange={set("phone")} data-testid="reg-phone-input"
              placeholder="+91 98xxxxxx" className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
          </Field>
          <Field label="Email">
            <Input required type="email" value={form.email} onChange={set("email")} data-testid="reg-email-input"
              className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
          </Field>
          <Field label="Password (6+ chars)">
            <Input required type="password" minLength={6} value={form.password} onChange={set("password")}
              data-testid="reg-password-input"
              className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
          </Field>
          <Field label={role === "driver" ? "Current city" : "City"}>
            <Input required value={form.city} onChange={set("city")} data-testid="reg-city-input"
              placeholder="Mumbai" className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
          </Field>
          <Field label={role === "sender" ? "Company (optional)" : "Fleet / Company (optional)"}>
            <Input value={form.company} onChange={set("company")} data-testid="reg-company-input"
              className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
          </Field>

          {role === "lorry_owner" && (
            <>
              <Field label="Vehicle type">
                <Select value={form.vehicle_type} onValueChange={(v) => setForm((f) => ({ ...f, vehicle_type: v }))}>
                  <SelectTrigger data-testid="reg-vehicle-type" className="h-12 bg-[#141414] border-[#2A2A2A] focus:ring-[#FF5A00] rounded-sm">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>{VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Vehicle number">
                <Input value={form.vehicle_number} onChange={set("vehicle_number")} data-testid="reg-vehicle-number"
                  placeholder="MH12AB1234" className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
              </Field>
              <Field label="Capacity (kg)">
                <Input type="number" value={form.vehicle_capacity_kg} onChange={set("vehicle_capacity_kg")}
                  data-testid="reg-vehicle-capacity" placeholder="10000"
                  className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
              </Field>
            </>
          )}

          {role === "driver" && (
            <>
              <Field label="Driving License (DL) number">
                <Input required value={form.dl_number} onChange={set("dl_number")} data-testid="reg-dl-number"
                  placeholder="HR01 20200012345"
                  className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FFD700] rounded-sm" />
              </Field>
              <Field label="DL registration date">
                <Input required type="date" value={form.dl_reg_date} onChange={set("dl_reg_date")}
                  data-testid="reg-dl-reg-date"
                  className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FFD700] rounded-sm" />
              </Field>
              <Field label="Home state">
                <Select value={form.home_state} onValueChange={(v) => setForm((f) => ({ ...f, home_state: v }))}>
                  <SelectTrigger data-testid="reg-home-state" className="h-12 bg-[#141414] border-[#2A2A2A] focus:ring-[#FFD700] rounded-sm">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>{INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Home town / city">
                <Input required value={form.home_city} onChange={set("home_city")} data-testid="reg-home-city"
                  placeholder="Rohtak" className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FFD700] rounded-sm" />
              </Field>
              <Field label="Driving experience (years)">
                <Input required type="number" min="0" value={form.experience_years} onChange={set("experience_years")}
                  data-testid="reg-experience" placeholder="5"
                  className="h-12 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FFD700] rounded-sm" />
              </Field>
            </>
          )}

          {err && (
            <div className="sm:col-span-2 flex items-center gap-2 p-3 bg-red-900/20 border border-red-900/40 text-red-300 text-sm rounded-sm" data-testid="register-error">
              <AlertCircle className="w-4 h-4" /> {err}
            </div>
          )}

          <div className="sm:col-span-2 flex items-center justify-between gap-6 pt-4">
            <div className="text-sm text-zinc-500">
              Already have an account?{" "}
              <Link to="/login" data-testid="register-login-link" className="text-[#FF5A00] font-semibold hover:underline">Log in</Link>
            </div>
            <Button type="submit" disabled={busy} data-testid="register-submit-btn"
              className="h-12 rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold px-8">
              {busy ? "Creating..." : "Create account"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="overline text-zinc-400">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function RoleTile({ active, onClick, icon: I, title, desc, testId, accent }) {
  const activeColor = accent ? "border-[#FFD700] bg-[#FFD700]/10" : "border-[#FF5A00] bg-[#FF5A00]/10";
  const activeIcon = accent ? "bg-[#FFD700] text-black" : "bg-[#FF5A00] text-black";
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`text-left p-5 border transition-colors ${active ? activeColor : "border-[#2A2A2A] bg-[#141414] hover:border-zinc-500"}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 flex items-center justify-center ${active ? activeIcon : "bg-[#0A0A0A] text-zinc-300"}`}>
          <I className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display font-bold text-lg">{title}</div>
          <div className="text-sm text-zinc-400">{desc}</div>
        </div>
      </div>
    </button>
  );
}
